import { useEffect, useRef, useState } from 'react'
import { useLogout } from '../../account-menu/hooks'
import { InactiveDialog } from './inactive-dialog'
import { useTranslation } from 'react-i18next'

export const InactiveDialogContainer = () => {
  const { t } = useTranslation()
  const INITIAL_TIME = 600
  const GRACE_TIME = 300

  const { handleLogout } = useLogout()
  const [openDialog, setOpenDialog] = useState(false)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [timerStatus, setTimerStatus] = useState('INITIAL')

  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const dialogRef = useRef<boolean>(false)

  const handleOpenDialog = () => {
    dialogRef.current = true
    setOpenDialog(true)
    setTimerStatus('GRACE')
  }

  const startTimer = () => {
    setTimerStatus('STARTED')
  }

  const stopTimer = () => {
    setTimerStatus('STOPPED')
  }

  const resetTimer = () => {
    setTimerStatus('RESET')
  }

  useEffect(() => {
    if (timerStatus === 'STARTED') {
      clearInterval(timerRef.current)
      timerRef.current = setInterval(() => {
        setElapsedTime((prevTime) => prevTime + 1)
      }, 1000)
    } else if (timerStatus === 'GRACE') {
      clearInterval(timerRef.current)
      timerRef.current = setInterval(() => {
        setElapsedTime((prevTime) => prevTime - 1)
      }, 1000)
    } else if (timerStatus === 'STOPPED') {
      clearInterval(timerRef.current)
      timerRef.current = null
      setElapsedTime(0)
    } else if (timerStatus === 'RESET') {
      clearInterval(timerRef.current)
      timerRef.current = null
      setElapsedTime(0)
      startTimer()
    }
  }, [timerStatus])

  useEffect(() => {
    if (
      process.env.ENVIRONMENT === 'LOCAL' ||
      process.env.ENVIRONMENT === 'DEV' ||
      process.env.ENVIRONMENT === 'STG'
    ) {
      console.log('TIMER', elapsedTime)
    }

    if (elapsedTime >= GRACE_TIME) {
      handleOpenDialog()
      setElapsedTime(GRACE_TIME)
    }

    if (timerStatus === 'GRACE' && elapsedTime <= 0) {
      localStorage.removeItem('inactivity_stamp')
      setElapsedTime(0)
      stopTimer()
      handleLogout()
    }
  }, [elapsedTime])

  const handleSetTime = () => {
    localStorage.setItem('inactivity_stamp', Date.now().toString())
  }

  const handleFocus = () => {
    const inactivityTime = localStorage.getItem('inactivity_stamp') || 0

    const seconds =
      Math.abs(
        new Date(Number(inactivityTime)).getTime() - new Date().getTime(),
      ) / 1000

    if (seconds > GRACE_TIME + INITIAL_TIME) {
      localStorage.removeItem('inactivity_stamp')
      setElapsedTime(0)
      handleLogout()
      return
    }

    if (seconds >= INITIAL_TIME) {
      const diff = seconds - GRACE_TIME
      setElapsedTime(GRACE_TIME - diff)
      handleOpenDialog()
    }
  }

  useEffect(() => {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        handleSetTime()
      } else {
        handleFocus()
      }
    })

    addEventListener('focus', handleFocus)
    addEventListener('blur', handleSetTime)
    addEventListener('mousemove', () => {
      if (!dialogRef.current) {
        setTimerStatus('RESET')
      }
    })

    return () => {
      removeEventListener('focus', handleFocus)
      removeEventListener('blur', handleSetTime)
      removeEventListener('mousemove', resetTimer)
    }
  }, [])

  useEffect(() => {
    startTimer()
  }, [])

  const title = t('inactivityDesc')
  const description = t('inactivityDesc1')

  const buttons = [
    {
      variant: 'tertiary',
      label: t('inactivityCancel'),
      size: 'small',
      event: () => {
        localStorage.removeItem('inactivity_stamp')
        handleLogout()
      },
    },
    {
      variant: 'primary',
      label: t('inactivityDone'),
      size: 'small',
      event: () => {
        setElapsedTime(0)
        setOpenDialog(false)
        setTimerStatus('STARTED')
        dialogRef.current = false
      },
    },
  ]

  return (
    <>
      <InactiveDialog
        title={title}
        description={description}
        elapsedTime={elapsedTime}
        grace={GRACE_TIME}
        buttons={buttons}
        handleOpenDialog={handleOpenDialog}
        openDialog={openDialog}
      />
    </>
  )
}
